import { ComponentType } from 'react';
import { FixedSizeListProps, FixedSizeList as _FixedSizeList_ } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  Button,
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@artstorefronts/ui';
import { Search, Edit, ExternalLink } from 'lucide-react';
import { useQuery, useMutation } from '@apollo/client';
import { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import AdminLayout from '../../../components/AdminLayout';
import { ADMIN_PROFILES_QUERY } from '../../../graphql/queries';
import { ADMIN_UPDATE_PROFILE } from '../../../graphql/mutations';

const List = _FixedSizeList_ as unknown as ComponentType<FixedSizeListProps>;

interface Profile {
  id: string;
  userId: string;
  avatarUrl?: string;
  bio?: string;
  displayName?: string;
  artistStatement?: string;
  countryCode?: string;
  country?: string;
  regionCode?: string;
  region?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  bannerUrl?: string;
  slug?: string;
  socialLinks?: any;
  isPublic?: boolean;
  isDiscoverable?: boolean;
  isDisabled?: boolean;
  isBanned?: boolean;
  isNsfw?: boolean;
  excludeFromSuggestions?: boolean;
  categories?: Array<{
    id: string;
    name: string;
    slug: string;
  }>;
  createdAt: string;
  updatedAt: string;
  user: {
    asfId: string;
    firstName?: string;
    lastName?: string;
    email: string;
    username?: string;
    createdAt: string;
    updatedAt: string;
  };
  images?: Array<{
    id: string;
    slug?: string;
    title?: string;
    description?: string;
    thumbnailUrl?: string;
    optimizedUrl?: string;
    createdAt: string;
  }>;
}

// Every moderation column is a bare Yes/No with no visible consequence, so the
// header carries the explanation. `cursor-help` plus the dotted underline are the
// only affordance that a description exists. The copy at `profiles.table.help.*`
// is written from the enforcement points, not the column name — several flags do
// something other than what they sound like. NSFW, for one, gates no viewer
// preference: `shared/profile-tab-visibility.ts` drops the Gallery and Room
// Mockups tabs for owner and visitor alike while leaving the profile itself
// public and discoverable.
function FlagHeader({ label, description }: { label: string; description: string }) {
  return (
    <div className="text-center">
      <HoverCard openDelay={150} closeDelay={100}>
        <HoverCardTrigger asChild>
          <span className="cursor-help underline decoration-dotted decoration-zinc-400 underline-offset-2">
            {label}
          </span>
        </HoverCardTrigger>
        {/* The grid sits inside two `overflow-hidden` wrappers (the card and
            AdminLayout's `max-w-7xl`), which clip a non-portalled popover. */}
        <HoverCardPortal>
          <HoverCardContent side="bottom" align="center" className="w-72">
            <p className="text-sm font-semibold">{label}</p>
            <p className="mt-1 text-xs font-normal leading-relaxed text-zinc-600">{description}</p>
          </HoverCardContent>
        </HoverCardPortal>
      </HoverCard>
    </div>
  );
}

export default function ProfilesPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [isDiscoverableFilter, setIsDiscoverableFilter] = useState<boolean | undefined>(undefined);
  const [isDisabledFilter, setIsDisabledFilter] = useState<boolean | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const loadingMoreRef = useRef(false);
  const PAGE_SIZE = 50;

  const getProfileUrl = (slug: string) => {
    return `${__TOWN_SQUARE_URL__}/${slug}`;
  };

  const { data, loading, fetchMore, refetch } = useQuery(ADMIN_PROFILES_QUERY, {
    variables: {
      limit: PAGE_SIZE,
      offset: 0,
      search: search || undefined,
      isDiscoverable: isDiscoverableFilter,
      isDisabled: isDisabledFilter,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [updateProfile] = useMutation(ADMIN_UPDATE_PROFILE);

  const allProfiles = data?.adminProfiles?.profiles || [];
  const totalProfiles = data?.adminProfiles?.total || 0;
  const hasMoreData = !search && allProfiles.length < totalProfiles && totalProfiles > 0;

  const loadMoreProfiles = useCallback(async () => {
    if (!hasMoreData || loading || loadingMore || allProfiles.length >= totalProfiles || loadingMoreRef.current) {
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          limit: PAGE_SIZE,
          offset: allProfiles.length,
          search: undefined, // Only load more when not searching
          isDiscoverable: isDiscoverableFilter,
          isDisabled: isDisabledFilter,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.adminProfiles?.profiles?.length) {
            return prev;
          }

          // Prevent duplicate profiles by checking if we already have them
          const existingIds = new Set(prev.adminProfiles.profiles.map((profile: any) => profile.id));
          const newProfiles = fetchMoreResult.adminProfiles.profiles.filter(
            (profile: any) => !existingIds.has(profile.id)
          );

          if (newProfiles.length === 0) {
            return prev;
          }

          const updatedProfiles = [...prev.adminProfiles.profiles, ...newProfiles];

          return {
            adminProfiles: {
              ...prev.adminProfiles,
              profiles: updatedProfiles,
              total: Math.max(prev.adminProfiles.total, fetchMoreResult.adminProfiles.total),
            },
          };
        },
      });
    } catch (error) {
      console.error('Error loading more profiles:', error);
    } finally {
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [
    fetchMore,
    allProfiles.length,
    hasMoreData,
    loading,
    loadingMore,
    totalProfiles,
    isDiscoverableFilter,
    isDisabledFilter,
  ]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const handleFilterChange = useCallback((value: boolean | undefined) => {
    setIsDiscoverableFilter(value);
  }, []);

  const handleProfileFieldChange = useCallback(
    async (
      profileId: string,
      field: 'isDiscoverable' | 'isDisabled' | 'isBanned' | 'isNsfw' | 'excludeFromSuggestions',
      value: boolean
    ) => {
      try {
        await updateProfile({
          variables: {
            input: { profileId, [field]: value },
          },
        });
        await refetch({
          limit: PAGE_SIZE,
          offset: 0,
          search: search || undefined,
          isDiscoverable: isDiscoverableFilter,
          isDisabled: isDisabledFilter,
        });
        toast.success(t('profiles.toast.updated'));
      } catch (error) {
        console.error('Error updating profile:', error);
        toast.error(t('profiles.toast.update-failed'));
      }
    },
    [updateProfile, refetch, search, isDiscoverableFilter, isDisabledFilter, t]
  );

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      const wasInputFocused = document.activeElement === searchInputRef.current;

      if (search && search.trim()) {
        // When searching, get all results (no pagination)
        await refetch({
          search: search.trim(),
          limit: undefined,
          offset: undefined,
          isDiscoverable: isDiscoverableFilter,
          isDisabled: isDisabledFilter,
        });
      } else {
        // When not searching, reset to paginated browsing
        await refetch({
          search: undefined,
          limit: PAGE_SIZE,
          offset: 0,
          isDiscoverable: isDiscoverableFilter,
          isDisabled: isDisabledFilter,
        });
      }

      // Restore focus if input was focused before refetch
      if (wasInputFocused && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [search, refetch, isDiscoverableFilter, isDisabledFilter]);

  const filteredProfiles = allProfiles.filter((profile: Profile) => {
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        profile.slug?.toLowerCase().includes(searchLower) || profile.user.email.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const profile = filteredProfiles[index] as Profile;

    // Load more data when approaching the end of the list (only when not searching)
    useEffect(() => {
      if (!search && index >= allProfiles.length - 5 && hasMoreData && !loadingMore && !loadingMoreRef.current) {
        loadMoreProfiles();
      }
    }, [index, allProfiles.length, hasMoreData, loadMoreProfiles, search, loadingMore]);

    if (!profile) return null;

    return (
      <div style={style} className="border-b border-zinc-200">
        <div className="grid grid-cols-[220px_150px_100px_100px_100px_100px_100px_100px_200px_1fr] py-3 px-4 text-sm hover:bg-zinc-50 items-center">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {profile.avatarUrl && (
                <img src={profile.avatarUrl} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
              )}
              <span className="font-medium truncate pr-4" title={profile.user.email}>
                {profile.user.email}
              </span>
            </div>
          </div>
          <div className="text-zinc-600">{profile.slug || t('profiles.no-slug')}</div>
          <div className="text-center flex justify-center">
            <Select
              value={profile.isDiscoverable?.toString() ?? 'false'}
              onValueChange={(value) => handleProfileFieldChange(profile.id, 'isDiscoverable', value === 'true')}
            >
              <SelectTrigger className="w-16 h-6 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">{tc('yes')}</SelectItem>
                <SelectItem value="false">{tc('no')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-center flex justify-center">
            <Select
              value={profile.excludeFromSuggestions?.toString() ?? 'false'}
              onValueChange={(value) =>
                handleProfileFieldChange(profile.id, 'excludeFromSuggestions', value === 'true')
              }
            >
              <SelectTrigger className="w-16 h-6 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">{tc('yes')}</SelectItem>
                <SelectItem value="false">{tc('no')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-center flex justify-center">
            <Select
              value={profile.isDisabled?.toString() ?? 'false'}
              onValueChange={(value) => handleProfileFieldChange(profile.id, 'isDisabled', value === 'true')}
            >
              <SelectTrigger className="w-16 h-6 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">{tc('yes')}</SelectItem>
                <SelectItem value="false">{tc('no')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-center flex justify-center">
            <Select
              value={profile.isBanned?.toString() ?? 'false'}
              onValueChange={(value) => handleProfileFieldChange(profile.id, 'isBanned', value === 'true')}
            >
              <SelectTrigger className="w-16 h-6 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">{tc('yes')}</SelectItem>
                <SelectItem value="false">{tc('no')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-center flex justify-center">
            <Select
              value={profile.isNsfw?.toString() ?? 'false'}
              onValueChange={(value) => handleProfileFieldChange(profile.id, 'isNsfw', value === 'true')}
            >
              <SelectTrigger className="w-16 h-6 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">{tc('yes')}</SelectItem>
                <SelectItem value="false">{tc('no')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-center">
            <span
              className={`px-2 py-1 rounded-full text-xs ${
                profile.isPublic ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
              }`}
            >
              {profile.isPublic ? tc('yes') : tc('no')}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {profile.images?.slice(0, 6).map((image) => (
              <img
                key={image.id}
                src={image.thumbnailUrl || image.optimizedUrl}
                alt={image.title || 'Image'}
                className="w-6 h-6 rounded object-cover"
                title={image.title || 'Image'}
              />
            ))}
            {(!profile.images || profile.images.length === 0) && (
              <span className="text-zinc-400 text-xs">{t('profiles.no-images')}</span>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            {profile.slug && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                title={t('profiles.view-profile')}
                onClick={() => window.open(getProfileUrl(profile.slug || ''), '_blank')}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="sm" asChild className="h-8 w-8 p-0" title={t('profiles.edit-profile')}>
              <Link to={`/admin/profiles/${profile.id}`}>
                <Edit className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout title={t('profiles.page-title')} description={t('profiles.page-description')}>
      <>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4 flex-1">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder={t('profiles.search-placeholder')}
                className="pl-8"
                value={search}
                onChange={handleSearchChange}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">{t('profiles.filter-discoverable')}</label>
              <Select
                value={isDiscoverableFilter === undefined ? 'all' : isDiscoverableFilter.toString()}
                onValueChange={(value) => handleFilterChange(value === 'all' ? undefined : value === 'true')}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{tc('all')}</SelectItem>
                  <SelectItem value="true">{tc('yes')}</SelectItem>
                  <SelectItem value="false">{tc('no')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">{t('profiles.filter-disabled')}</label>
              <Select
                value={isDisabledFilter === undefined ? 'all' : isDisabledFilter.toString()}
                onValueChange={(value) => setIsDisabledFilter(value === 'all' ? undefined : value === 'true')}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{tc('all')}</SelectItem>
                  <SelectItem value="true">{tc('yes')}</SelectItem>
                  <SelectItem value="false">{tc('no')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            {search
              ? t('profiles.count', { count: filteredProfiles.length })
              : t('profiles.count-paginated', { loaded: allProfiles.length, total: totalProfiles })}
          </div>
        </div>

        <div className="rounded-md border bg-white overflow-hidden">
          <div className="bg-zinc-50 border-b">
            <div className="grid grid-cols-[220px_150px_100px_100px_100px_100px_100px_100px_200px_1fr] py-3 px-4 text-xs font-medium">
              <div>{t('profiles.table.user')}</div>
              <div>{t('profiles.table.slug')}</div>
              <FlagHeader
                label={t('profiles.table.discoverable')}
                description={t('profiles.table.help.discoverable')}
              />
              <FlagHeader label={t('profiles.table.excluded')} description={t('profiles.table.help.excluded')} />
              <FlagHeader label={t('profiles.table.disabled')} description={t('profiles.table.help.disabled')} />
              <FlagHeader label={t('profiles.table.banned')} description={t('profiles.table.help.banned')} />
              <FlagHeader label={t('profiles.table.nsfw')} description={t('profiles.table.help.nsfw')} />
              <div className="text-center">{t('profiles.table.public')}</div>
              <div className="text-center">{t('profiles.table.images')}</div>
              <div className="text-right">{t('profiles.table.actions')}</div>
            </div>
          </div>
          <div style={{ height: 'calc(100dvh - 500px)' }} data-testid="profiles-list">
            <AutoSizer>
              {({ height, width }) => (
                <List height={height} itemCount={filteredProfiles.length} itemSize={56} width={width}>
                  {Row}
                </List>
              )}
            </AutoSizer>
          </div>
          {loadingMore && (
            <div className="flex items-center justify-center py-1">
              <span className="text-xs text-muted-foreground">{tc('loading')}</span>
            </div>
          )}
        </div>
      </>
    </AdminLayout>
  );
}
